import { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { History, RefreshCw, Search, X } from "lucide-react";
import { DataTablePagination } from "@/pages/conciliation/dtef-validation/components/data-table-pagination";
import {
  useGetDtefImportHistoryUnifiedQuery,
  useDeleteDtefBatchMutation,
  useLazyGetBatchDetailsQuery,
  type DtefImportHistoryItem,
  type DtefImportHistoryOrderBy,
  type DtefImportHistoryOrderDir,
  type DtefImportHistoryQuery,
  type DtefImportHistoryStatus,
  type DtefImportHistoryType,
} from "@/services/conciliation/dtfe-import";
import { getImportHistoryColumns } from "./import-history-columns";
import { ConfirmActionModal } from "./duplicate-confirm-modal";
import { BatchDetailsModal } from "./batch-details-modal";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { toast } from "components/ui/use-toast";

interface FilterState {
  dateFrom: string;
  dateTo: string;
  type: DtefImportHistoryType;
  status: DtefImportHistoryStatus;
  actorEmail: string;
  fileName: string;
}

const INITIAL_FILTERS: FilterState = {
  dateFrom: "",
  dateTo: "",
  type: "all",
  status: "all",
  actorEmail: "",
  fileName: "",
};

const getTypeForRow = (row: DtefImportHistoryItem): "pix" | "payment" =>
  row.entity_type === "PIX_DTEF_IMPORT" ? "pix" : "payment";

export const ImportHistoryTable = () => {
  const { handleModal } = useDialogModal();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [orderBy, setOrderBy] = useState<DtefImportHistoryOrderBy>("created_at");
  const [orderDir, setOrderDir] = useState<DtefImportHistoryOrderDir>("desc");

  const [pendingFilters, setPendingFilters] =
    useState<FilterState>(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] =
    useState<FilterState>(INITIAL_FILTERS);

  const queryArgs = useMemo<DtefImportHistoryQuery>(
    () => ({
      page,
      limit: pageSize,
      type: appliedFilters.type,
      status: appliedFilters.status,
      dateFrom: appliedFilters.dateFrom || undefined,
      dateTo: appliedFilters.dateTo || undefined,
      actorEmail: appliedFilters.actorEmail || undefined,
      fileName: appliedFilters.fileName || undefined,
      orderBy,
      orderDir,
    }),
    [page, pageSize, appliedFilters, orderBy, orderDir],
  );

  const { data, isLoading, isFetching, refetch } =
    useGetDtefImportHistoryUnifiedQuery(queryArgs, {
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    });

  const [deleteBatch] = useDeleteDtefBatchMutation();
  const [triggerGetDetails] = useLazyGetBatchDetailsQuery();

  const allData = data?.data ?? [];
  const vigenteBatchId = allData.find(item => item.is_active)?.entity_id;

  const closeModal = () =>
    handleModal({ isOpen: false, element: null, title: "" });

  const handleDelete = (batchId: string, row: DtefImportHistoryItem) => {
    handleModal({
      isOpen: true,
      title: "Excluir importação",
      element: (
        <ConfirmActionModal
          actionLabel="excluir este lote de registros"
          onConfirm={async () => {
            closeModal();
            try {
              const result = await deleteBatch({
                batchId,
                type: getTypeForRow(row),
              }).unwrap();
              toast({ description: result.message });
            } catch (err) {
              const error = err as {
                status?: number;
                data?: { code?: string; message?: string };
              };
              if (error?.status === 409) {
                toast({
                  variant: "destructive",
                  description:
                    error.data?.message ??
                    "Este lote já foi modificado por outra sessão.",
                });
                refetch();
              } else {
                toast({
                  variant: "destructive",
                  description: "Erro ao excluir lote.",
                });
              }
            }
          }}
          onCancel={closeModal}
        />
      ),
      onClose: closeModal,
    });
  };

  const handleViewDetails = (batchId: string, row: DtefImportHistoryItem) => {
    const type = getTypeForRow(row);
    handleModal({
      isOpen: true,
      title: "Detalhes por Loja",
      element: (
        <BatchDetailsModal
          dtef_payment={[]}
          pix_dtef={[]}
          isLoading
          type={type}
        />
      ),
      onClose: closeModal,
    });
    triggerGetDetails({ batchId, type })
      .unwrap()
      .then(details => {
        handleModal({
          isOpen: true,
          title: "Detalhes por Loja",
          element: (
            <BatchDetailsModal
              dtef_payment={details.dtef_payment}
              pix_dtef={details.pix_dtef}
              isLoading={false}
              type={type}
            />
          ),
          onClose: closeModal,
        });
      })
      .catch(() => {
        closeModal();
        toast({
          variant: "destructive",
          description: "Erro ao carregar detalhes.",
        });
      });
  };

  const handleSort = (field: DtefImportHistoryOrderBy) => {
    if (orderBy === field) {
      setOrderDir(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(field);
      setOrderDir("desc");
    }
    setPage(1);
  };

  const columns = useMemo(
    () =>
      getImportHistoryColumns({
        vigenteBatchId,
        orderBy,
        orderDir,
        onSort: handleSort,
        onDelete: batchId => {
          const row = allData.find(r => r.entity_id === batchId);
          if (row) handleDelete(batchId, row);
        },
        onViewDetails: batchId => {
          const row = allData.find(r => r.entity_id === batchId);
          if (row) handleViewDetails(batchId, row);
        },
      }),
    [vigenteBatchId, allData, orderBy, orderDir],
  );

  const table = useReactTable({
    data: allData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: data?.pagination.total ?? 0,
    state: {
      pagination: { pageIndex: page - 1, pageSize },
    },
    onPaginationChange: updater => {
      const prev = { pageIndex: page - 1, pageSize };
      const next = typeof updater === "function" ? updater(prev) : updater;
      const sizeChanged = next.pageSize !== prev.pageSize;
      setPage(sizeChanged ? 1 : next.pageIndex + 1);
      setPageSize(next.pageSize);
    },
  });

  const applyFilters = () => {
    setAppliedFilters(pendingFilters);
    setPage(1);
  };

  const clearFilters = () => {
    setPendingFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    setPage(1);
  };

  const hasActiveFilters =
    appliedFilters.dateFrom ||
    appliedFilters.dateTo ||
    appliedFilters.type !== "all" ||
    appliedFilters.status !== "all" ||
    appliedFilters.actorEmail ||
    appliedFilters.fileName;

  const updateFilter = <K extends keyof FilterState>(
    key: K,
    value: FilterState[K],
  ) => {
    setPendingFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Card className="w-full mt-6">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-[#613F4F]" />
            <CardTitle className="text-lg text-[#613F4F]">
              Histórico de Importações
            </CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            title="Atualizar histórico"
          >
            <RefreshCw
              className={`h-4 w-4 text-[#613F4F] ${isFetching ? "animate-spin" : ""}`}
            />
          </Button>
        </div>

        <form
          onSubmit={e => {
            e.preventDefault();
            applyFilters();
          }}
          className="flex flex-wrap items-end gap-3 mt-3"
        >
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-[#613F4F]">De</label>
            <Input
              type="date"
              value={pendingFilters.dateFrom}
              onChange={e => updateFilter("dateFrom", e.target.value)}
              className="w-[160px] h-9 border-[#613F4F]/20 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-[#613F4F]">Até</label>
            <Input
              type="date"
              value={pendingFilters.dateTo}
              onChange={e => updateFilter("dateTo", e.target.value)}
              className="w-[160px] h-9 border-[#613F4F]/20 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-[#613F4F]">Tipo</label>
            <Select
              value={pendingFilters.type}
              onValueChange={value =>
                updateFilter("type", value as DtefImportHistoryType)
              }
            >
              <SelectTrigger className="w-[160px] h-9 border-[#613F4F]/20 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="payment">Crédito/Débito</SelectItem>
                <SelectItem value="pix">PIX</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-[#613F4F]">Status</label>
            <Select
              value={pendingFilters.status}
              onValueChange={value =>
                updateFilter("status", value as DtefImportHistoryStatus)
              }
            >
              <SelectTrigger className="w-[160px] h-9 border-[#613F4F]/20 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Ativos</SelectItem>
                <SelectItem value="deleted">Excluídos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-[#613F4F]">
              Usuário
            </label>
            <Input
              type="text"
              placeholder="email@empresa.com"
              value={pendingFilters.actorEmail}
              onChange={e => updateFilter("actorEmail", e.target.value)}
              className="w-[200px] h-9 border-[#613F4F]/20 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-[#613F4F]">
              Arquivo
            </label>
            <Input
              type="text"
              placeholder="nome do arquivo"
              value={pendingFilters.fileName}
              onChange={e => updateFilter("fileName", e.target.value)}
              className="w-[180px] h-9 border-[#613F4F]/20 text-sm"
            />
          </div>
          <Button
            type="submit"
            disabled={isFetching}
            className="h-9 bg-[#F6D040] text-[#613F4F] hover:bg-[#F6D040]/90"
          >
            <Search className="mr-2 h-4 w-4" />
            Consultar
          </Button>
          {hasActiveFilters && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-9 text-xs text-[#613F4F]"
            >
              <X className="mr-1 h-3 w-3" />
              Limpar filtros
            </Button>
          )}
        </form>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map(hg => (
                <TableRow key={hg.id}>
                  {hg.headers.map(h => (
                    <TableHead key={h.id}>
                      {h.isPlaceholder
                        ? null
                        : flexRender(
                            h.column.columnDef.header,
                            h.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : allData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    Nenhum registro encontrado.
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map(row => (
                  <TableRow
                    key={row.id}
                    className={
                      row.original.entity_id === vigenteBatchId
                        ? "bg-green-50"
                        : undefined
                    }
                  >
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <div className="mt-4">
          <DataTablePagination table={table} />
        </div>
      </CardContent>
    </Card>
  );
};
